import type { ExperienceLayouts } from '@/types/gql.generated';
import {
  CUSTOMIZE_HOME_EDIT_VALUE,
  isCustomizeControlId,
  MAGIC_EDIT_VALUE,
  type CustomizeControlId,
} from './customizeOptions';
import { useCustomizeParams } from './useCustomizeParams';

/** Whether the Add-a-view flow is open and in what mode. */
export type AddViewMode = 'create' | 'change' | null;

// Customize-mode UI state, all of it in the URL so every screen is deep-linkable
// and survives a refresh. `activeControlId` comes from `?edit=<id>` and drives
// panel selection; `isCustomizeModeActive` is true whenever the mode overlay is
// shown, including the home screen (`?edit=all`) where no panel is selected.
export const useCustomizeMode = () => {
  const {
    action,
    setAction,
    edit,
    isAddingView,
    layout,
    setEdit,
    openEditTab,
    openMagicWorkflow,
    exitCustomize,
    openCustomizeHome,
    openAddView,
    openViewsWithAddView,
    openSourcesWithAdd,
    openChangeLayout,
    selectConfig,
    selectChangeLayout,
    clearLayout,
    closeAddView,
    finishCreate,
  } = useCustomizeParams();
  // `magic` rides on the same `edit` param but is not a customize control, so it
  // leaves activeControlId null (the add-content panel renders instead of the
  // customize sidebar/bar).
  const activeControlId = isCustomizeControlId(edit) ? edit : null;
  // isCustomizeModeActive is separate from activeControlId: it is true on the home
  // screen (no panel selected yet) as well as when a panel is open.
  const isCustomizeModeActive =
    edit === CUSTOMIZE_HOME_EDIT_VALUE || activeControlId !== null;
  const isMagicMode = edit === MAGIC_EDIT_VALUE;

  const addViewMode: AddViewMode =
    activeControlId === 'views'
      ? isAddingView
        ? 'create'
        : layout?.mode === 'change'
          ? 'change'
          : null
      : null;

  // The candidate layout previewed live in the planner while the picker is open
  // (ChildContent applies it over the active child via setPreview). Both modes
  // carry the layout id on the parsed `layout` selection (create captures it
  // alongside the config at pick time). Null when nothing is selected.
  const previewLayoutId: ExperienceLayouts | null =
    (addViewMode === 'create' && layout?.mode === 'create') ||
    (addViewMode === 'change' && layout?.mode === 'change')
      ? layout.layoutId
      : null;

  // Removing `layout` while keeping `view=<childId>` closes the change picker.
  const closeChangeLayout = clearLayout;

  // Customize always opens on the home card list rather than resuming the last
  // panel: the thing you want to change next is rarely the thing you changed
  // last, and the list doubles as a reminder of what's on offer.
  // `edit=all` replaces a lingering edit=magic and clears a Send action —
  // Customize, Magic, and Send are mutually exclusive.
  const goToCustomizeHome = openCustomizeHome;

  // Entering customize mode and returning to its home list are the same action.
  const enterCustomizeMode = goToCustomizeHome;

  // exitCustomize strips edit + source + tab + layout (and a lingering view=add).
  const exitCustomizeMode = exitCustomize;

  // Opening Magic writes `edit=magic` over any customize screen, home included.
  // Stable, so the store→URL bridge effect in AddExperienceContentPanel has
  // stable deps.
  const enterMagicWorkflow = openMagicWorkflow;
  const exitMagicMode = exitCustomize;

  // Send is a takeover flow like customize mode and Magic; setting an action
  // clears `edit`, so it leaves customize the same way. Stable: callers hold it
  // in effect deps.
  const setInteraction = setAction;

  const setActiveControl = (id: CustomizeControlId | null) => {
    if (id === null) {
      exitCustomizeMode();
      return;
    }
    // setEdit clears the panel sub-params (source/tab/layout), so any in-progress
    // layout selection is dropped when switching panels — ChildContent clears
    // the layout preview once previewLayoutId derives to null.
    setEdit(id);
  };

  // setActiveControl for a panel with sub-navigation: lands on a specific tab
  // rather than the panel's default section. Used by deep links from outside
  // customize mode (e.g. the planner filter popover's Manage links).
  const setActiveControlTab = (id: CustomizeControlId, tab: string) =>
    openEditTab(id, tab);

  return {
    action,
    edit,
    setAction: setInteraction,
    activeControlId,
    addViewMode,
    clearLayout,
    closeAddView,
    closeChangeLayout,
    enterCustomizeMode,
    enterMagicWorkflow,
    exitCustomizeMode,
    exitMagicMode,
    finishCreate,
    goToCustomizeHome,
    isCustomizeModeActive,
    isMagicMode,
    layout,
    openAddView,
    openChangeLayout,
    openSourcesWithAdd,
    openViewsWithAddView,
    previewLayoutId,
    selectChangeLayout,
    selectConfig,
    setActiveControl,
    setActiveControlTab,
  };
};
